import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet";
import { RouteComponentProps } from "react-router";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Button from "../../../components/Button";
import Error from "../../../components/Error";
import Info from "../../../components/Info";
import Loading from "../../../components/Loading";
import WeduTooltip from "../../../components/WeduTooltip/intex";
import { useWizard } from "../../wizard/context";
import { endOfTitle } from "../../../constants/constants";
import { useLanguageCoursesPage } from "../hooks/useLanguageCoursesPage";
import { ILanguageCoursesContext } from "../types";
import LanguageCoursesFilter from "../components/LanguageCoursesFilter";
import LanguageCoursesFilterForm from "../components/LanguageCoursesFilterForm";
import { Language } from "../components/Language";
import { useCheckForViewModal } from "../../academic/hooks/useCheckForViewModal";

const LanguageCoursesContext = createContext({} as ILanguageCoursesContext);
export const useLanguageCoursesContext = (): ILanguageCoursesContext =>
  useContext(LanguageCoursesContext);

const useStyles = makeStyles(() => ({
  root: {
    position: "relative",
  },
  container: {
    padding: "unset",
    marginTop: 24,
  },
  listContainer: {
    border: "1px solid #e8eaf0",
    borderRadius: 15,
    overflow: "hidden",
    "& .MuiPaper-elevation1": {
      padding: "unset",
    },
    minHeight: 480,
    backgroundColor: "#ffffff",
  },
  buttonContainer: {
    padding: "unset",
    textAlign: "right",
  },
  stickyContainer: {
    position: "sticky",
    top: 120,
  },
  button: {
    maxWidth: 280,
    fontSize: 16,
  },
  info: {
    height: 400,
  },
}));

const LanguageCoursesPage: FC<RouteComponentProps> = () => {
  const classes = useStyles();

  const { openWizard } = useWizard();
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
  const {
    isLoading,
    isError,
    errorMessage,
    applyFilter,
    languages,
    refetchLanguageCourses,
    languageCourseWizardConfigs,
  } = useLanguageCoursesPage();

  useCheckForViewModal({
    getViewCourseConfig: languageCourseWizardConfigs.getViewConfig,
  });

  const submitHandler = useCallback((data) => {
    setIsFilterApplied(true);
    applyFilter(data);
  }, []);

  const contextValue = useRef<ILanguageCoursesContext>({
    refetchLanguageCourses,
    languageCourseWizardConfigs,
    submitHandler,
  });

  const addCourse = useCallback(async () => {
    (await openWizard(languageCourseWizardConfigs.getCreateConfig())) &&
      (await refetchLanguageCourses());
  }, []);

  return (
    <>
      <Helmet>
        <title>Courses{endOfTitle}</title>
      </Helmet>
      <LanguageCoursesContext.Provider value={contextValue.current}>
        {isLoading && <Loading />}
        <Container maxWidth={"lg"} className={classes.root}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <div className={classes.stickyContainer}>
                <LanguageCoursesFilter
                  submitHandler={submitHandler}
                  filterForm={LanguageCoursesFilterForm}
                />
              </div>
            </Grid>
            <Grid item xs={12} md={9}>
              {!isLoading && (
                <>
                  <Container maxWidth="lg" className={classes.buttonContainer}>
                    <WeduTooltip title="Add course">
                      <Button
                        color={"success"}
                        variant={"contained"}
                        onClick={addCourse}
                        className={classes.button}
                      >
                        Add
                      </Button>
                    </WeduTooltip>
                  </Container>
                  {isError ? (
                    <Error text={errorMessage} />
                  ) : languages.length > 0 || isFilterApplied ? (
                    <Container maxWidth="lg" className={classes.container}>
                      {languages.length > 0 ? (
                        <div className={classes.listContainer}>
                          {languages.map((language) => (
                            <Language key={language.id} language={language} />
                          ))}
                        </div>
                      ) : (
                        <Info
                          className={classes.info}
                          icon={"search"}
                          text={"You have no results for now."}
                        />
                      )}
                    </Container>
                  ) : (
                    <Container maxWidth="lg" className={classes.container}>
                      <Info
                        className={classes.info}
                        text={
                          "Step 1: Create Branches.\nStep 2: Complete Courses and assign to branches."
                        }
                      />
                    </Container>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Container>
      </LanguageCoursesContext.Provider>
    </>
  );
};

export default LanguageCoursesPage;
